/**
 * SH-FILES-002 — Client-side file validation utilities.
 *
 * Pure functions, zero React imports. The companion hook
 * `useFileValidation` (`@dfq/shared/hooks/useFileValidation`) wraps
 * these so containers / capsules can drive inline error UI from a
 * single source of truth.
 *
 * Per `.claude/rules/frontend-data-flow.md`, this file lives in the
 * `lib/` folder because it has no React surface, no Redux dependency,
 * no router awareness, and no auth context. It can be exercised in a
 * plain Node test without any provider wrappers.
 *
 * Validation pipeline (run in order, short-circuiting only when a
 * blocker fires):
 *
 *   1. Extension check vs `accepted_types_json`
 *   2. MIME check vs `EXTENSION_MIME_MAP`
 *   3. Size check vs `max_file_size_mb`
 *   4. Magic-byte sniff (warning, never blocking) — verifies the
 *      declared extension matches the actual file signature.
 *
 * The DPI / color-mode / help_text / special_rules data is
 * informational — those are surfaced via the hook's render helpers,
 * not enforced by validation logic.
 */

// ---------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------

/**
 * Per-product upload-rule shape. Mirrors the
 * `FileUploadRule` entity persisted on the platform side and exposed
 * via the product DTO. Snake-case field names are kept verbatim so
 * the wire payload can flow into the hook without an adapter.
 */
export interface FileUploadRule {
  /** File extensions accepted, lowercase, no leading dot — e.g. ['pdf', 'tif']. */
  accepted_types_json: string[];
  /** Maximum per-file size in megabytes (decimal MB, base 1_000_000). Null = unlimited. */
  max_file_size_mb: number | null;
  /** Minimum DPI hint — surfaced as an info banner only. Null = no hint. */
  min_dpi: number | null;
  /** Preferred DPI hint, above the minimum — surfaced as an info banner only. Null = no hint. */
  preferred_dpi: number | null;
  /** Required color mode hint — surfaced as an info banner only. Null = no hint. */
  required_color_mode: 'CMYK' | 'RGB' | null;
  /** Free-form per-rule text shown above the drop zone. Null = none. */
  help_text: string | null;
  /** Bullet-list of additional product-specific rules. Empty = none. */
  special_rules_json: string[];
}

/**
 * Validation error codes. `extension`, `mime`, and `size` are blockers
 * (the file should be rejected); `magic_bytes` is a warning the user
 * may choose to dismiss.
 */
export type ValidationErrorCode =
  | 'extension'
  | 'mime'
  | 'size'
  | 'magic_bytes';

export interface ValidationError {
  code: ValidationErrorCode;
  message: string;
}

export interface ValidationWarning {
  code: ValidationErrorCode;
  message: string;
}

export interface FileValidationResult {
  valid: boolean;
  errors: ValidationError[];
  warnings: ValidationWarning[];
}

// ---------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------

/**
 * Single source of truth mapping lowercase extension → expected MIME
 * types. A list-of-strings on the value side because some extensions
 * legitimately produce multiple MIMEs across browsers (e.g. `.jpg`
 * may be `image/jpeg` or `image/pjpeg` on legacy IE; `.tif` may be
 * `image/tiff` or `image/tif`).
 */
export const EXTENSION_MIME_MAP: Readonly<Record<string, readonly string[]>> = Object.freeze({
  pdf: ['application/pdf'],
  ai: ['application/postscript', 'application/illustrator', 'application/pdf'],
  eps: ['application/postscript', 'application/eps', 'image/eps'],
  psd: ['image/vnd.adobe.photoshop', 'application/x-photoshop', 'image/x-psd'],
  tif: ['image/tiff', 'image/tif'],
  tiff: ['image/tiff', 'image/tif'],
  jpg: ['image/jpeg', 'image/pjpeg'],
  jpeg: ['image/jpeg', 'image/pjpeg'],
  png: ['image/png'],
  gif: ['image/gif'],
  webp: ['image/webp'],
  svg: ['image/svg+xml'],
  zip: ['application/zip', 'application/x-zip-compressed'],
});

/**
 * Magic-byte signatures. First N bytes of the file are matched
 * against these; the first match (if any) is the inferred true type.
 *
 * Hex values:
 *   PDF      25 50 44 46           %PDF
 *   TIFF LE  49 49 2A 00           II*\0
 *   TIFF BE  4D 4D 00 2A           MM\0*
 *   ZIP/PSD-container 50 4B 03 04  PK\3\4 (also AI when wrapped, ZIP archives)
 *   PSD raw  38 42 50 53           8BPS
 *   JPEG     FF D8 FF              JPEG SOI marker
 *   PNG      89 50 4E 47           PNG header
 *   GIF      47 49 46 38           GIF8 (87a / 89a both start GIF8)
 *   WEBP     52 49 46 46 .. .. .. .. 57 45 42 50  RIFF...WEBP
 *
 * `inferredExtensions` is the set of extensions this signature is
 * legitimately associated with — e.g. PDF magic also identifies
 * many `.ai` files (Adobe Illustrator wraps PDF), so a `.ai`
 * upload that starts `25 50 44 46` is fine.
 */
interface MagicSignature {
  /** Stable identifier for diagnostics. */
  name: string;
  /** Byte sequence to match starting at `offset`. */
  bytes: readonly number[];
  /** Where in the file `bytes` should appear. Default 0. */
  offset?: number;
  /** Extensions whose declared type this signature legitimately satisfies. */
  inferredExtensions: readonly string[];
}

const MAGIC_SIGNATURES: readonly MagicSignature[] = [
  {
    name: 'PDF',
    bytes: [0x25, 0x50, 0x44, 0x46],
    inferredExtensions: ['pdf', 'ai'],
  },
  {
    name: 'TIFF (LE)',
    bytes: [0x49, 0x49, 0x2a, 0x00],
    inferredExtensions: ['tif', 'tiff'],
  },
  {
    name: 'TIFF (BE)',
    bytes: [0x4d, 0x4d, 0x00, 0x2a],
    inferredExtensions: ['tif', 'tiff'],
  },
  {
    name: 'ZIP / PSD-container',
    bytes: [0x50, 0x4b, 0x03, 0x04],
    // Modern PSDs and AIs are sometimes shipped as ZIP-wrapped
    // archives. Don't fight the user when the wrapper is legitimate.
    inferredExtensions: ['zip', 'psd', 'ai'],
  },
  {
    name: 'PSD',
    bytes: [0x38, 0x42, 0x50, 0x53],
    inferredExtensions: ['psd'],
  },
  {
    name: 'JPEG',
    bytes: [0xff, 0xd8, 0xff],
    inferredExtensions: ['jpg', 'jpeg'],
  },
  {
    name: 'PNG',
    bytes: [0x89, 0x50, 0x4e, 0x47],
    inferredExtensions: ['png'],
  },
  {
    name: 'GIF',
    bytes: [0x47, 0x49, 0x46, 0x38],
    inferredExtensions: ['gif'],
  },
  {
    name: 'WEBP',
    // RIFF....WEBP — match RIFF at offset 0 AND WEBP at offset 8.
    bytes: [0x52, 0x49, 0x46, 0x46],
    inferredExtensions: ['webp'],
  },
  {
    name: 'EPS (PostScript)',
    bytes: [0x25, 0x21, 0x50, 0x53],
    inferredExtensions: ['eps', 'ai'],
  },
];

/** Number of leading bytes the magic-sniff helper reads. */
export const MAGIC_BYTE_PREFIX_LENGTH = 12;

// ---------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------

/**
 * Extract the lowercase extension (no dot) from a filename. Returns
 * the empty string when the filename has no dot or the dot is the
 * last character. Multi-dot names take the trailing segment, so
 * `archive.tar.gz` resolves to `gz`.
 */
export function getFileExtension(filename: string): string {
  const dotIndex = filename.lastIndexOf('.');
  if (dotIndex < 0 || dotIndex === filename.length - 1) return '';
  return filename.slice(dotIndex + 1).toLowerCase();
}

/**
 * Normalise an `accepted_types_json` entry — strip a leading dot and
 * lowercase. Tolerant of `[".PDF", "tiff"]` style mixed input.
 */
function normaliseAcceptedType(entry: string): string {
  const trimmed = entry.trim().toLowerCase();
  return trimmed.startsWith('.') ? trimmed.slice(1) : trimmed;
}

/**
 * Validate the file's extension against the rule's accepted list.
 * Pure, synchronous.
 */
export function validateExtension(
  file: File,
  acceptedTypes: readonly string[],
): ValidationError | null {
  const ext = getFileExtension(file.name);
  const allowed = acceptedTypes.map(normaliseAcceptedType).filter(Boolean);
  if (allowed.length === 0) return null;
  if (!ext) {
    return {
      code: 'extension',
      message: `"${file.name}" has no file extension. Accepted types: ${allowed
        .map((t) => `.${t}`)
        .join(', ')}.`,
    };
  }
  if (!allowed.includes(ext)) {
    return {
      code: 'extension',
      message: `"${file.name}" is a .${ext} file. Accepted types: ${allowed
        .map((t) => `.${t}`)
        .join(', ')}.`,
    };
  }
  return null;
}

/**
 * Cross-check the browser-reported MIME against the
 * extension→MIME map. When the browser reports an empty MIME (some
 * `.psd` / `.ai` uploads on Linux Chrome surface as `''`), the
 * extension wins and no error is raised — magic-byte sniffing will
 * still catch a renamed binary.
 */
export function validateMime(file: File): ValidationError | null {
  const ext = getFileExtension(file.name);
  if (!ext) return null; // extension check handled by validateExtension

  const expected = EXTENSION_MIME_MAP[ext];
  if (!expected) return null; // unknown extension — extension check has the final say

  const reported = (file.type ?? '').toLowerCase();
  if (reported === '') return null;

  if (!expected.includes(reported)) {
    return {
      code: 'mime',
      message: `"${file.name}" reports MIME type "${reported}" but a .${ext} file should be ${expected.join(' or ')}.`,
    };
  }
  return null;
}

/**
 * Enforce the rule's max file size. Uses decimal megabytes
 * (1_000_000 bytes) to match the user-visible "{N} MB" copy.
 */
export function validateSize(
  file: File,
  maxFileSizeMb: number | null,
): ValidationError | null {
  if (maxFileSizeMb === null || maxFileSizeMb === undefined) return null;
  const limitBytes = maxFileSizeMb * 1_000_000;
  if (file.size <= limitBytes) return null;
  const actualMb = file.size / 1_000_000;
  return {
    code: 'size',
    message: `"${file.name}" is ${actualMb.toFixed(1)} MB, exceeding the ${maxFileSizeMb} MB limit.`,
  };
}

/**
 * Read the first {@link MAGIC_BYTE_PREFIX_LENGTH} bytes of the file
 * and return them as a `Uint8Array`. Pulled out so tests can stub a
 * fake `File` whose `arrayBuffer()` returns a predetermined buffer
 * without standing up a real `FileReader`.
 */
export async function readFilePrefix(
  file: Pick<File, 'slice'>,
  length: number = MAGIC_BYTE_PREFIX_LENGTH,
): Promise<Uint8Array> {
  const slice = file.slice(0, length);
  // `Blob.arrayBuffer()` is available in modern browsers AND the
  // jsdom test environment. Falling back to `FileReader` only if the
  // shorter API is missing.
  if (typeof (slice as Blob).arrayBuffer === 'function') {
    const buffer = await (slice as Blob).arrayBuffer();
    return new Uint8Array(buffer);
  }
  return new Promise<Uint8Array>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (result instanceof ArrayBuffer) {
        resolve(new Uint8Array(result));
      } else {
        reject(new Error('FileReader returned a non-ArrayBuffer result.'));
      }
    };
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed.'));
    reader.readAsArrayBuffer(slice as Blob);
  });
}

function bytesMatch(
  haystack: Uint8Array,
  needle: readonly number[],
  offset = 0,
): boolean {
  if (haystack.length < offset + needle.length) return false;
  for (let i = 0; i < needle.length; i += 1) {
    if (haystack[offset + i] !== needle[i]) return false;
  }
  return true;
}

/**
 * Scan a byte prefix against the known {@link MAGIC_SIGNATURES} list.
 * Returns the first matching signature, or `null` when nothing matches
 * (e.g. SVG, plain text, an unknown format).
 *
 * For the WEBP signature, also confirms the `WEBP` marker at offset
 * 8 so we don't false-match unrelated RIFF containers (AVI, WAV).
 */
export function matchMagicSignature(prefix: Uint8Array): MagicSignature | null {
  for (const signature of MAGIC_SIGNATURES) {
    if (!bytesMatch(prefix, signature.bytes, signature.offset ?? 0)) continue;
    if (signature.name === 'WEBP') {
      // Confirm "WEBP" at offset 8.
      const webp = [0x57, 0x45, 0x42, 0x50];
      if (!bytesMatch(prefix, webp, 8)) continue;
    }
    return signature;
  }
  return null;
}

/**
 * Magic-byte sniff. ALWAYS returns a warning (not an error) — the
 * upload pipeline never blocks on a magic mismatch because:
 *
 *   - Many design files (especially `.ai`, `.psd`) wrap multiple
 *     possible signatures.
 *   - Certain types (`.svg`, `.eps` text-mode) have no fixed magic.
 *   - The platform side runs a stronger server-side sniff anyway.
 *
 * The warning surfaces in the UI as a non-blocking notice so the
 * user can decide whether to upload anyway (rare, but legitimate
 * for hand-edited or tool-rewritten files).
 */
export async function validateMagicBytes(
  file: File,
): Promise<ValidationWarning | null> {
  const ext = getFileExtension(file.name);
  if (!ext) return null;

  // No expected map entry → can't form an opinion (e.g. unknown ext).
  // Likewise for `.svg`: text-based, magic-byte sniff is not a useful signal.
  if (!EXTENSION_MIME_MAP[ext]) return null;
  if (ext === 'svg') return null;

  let prefix: Uint8Array;
  try {
    prefix = await readFilePrefix(file);
  } catch {
    // If we can't read the prefix at all, surface a soft warning so
    // the user knows we couldn't verify the binary contents.
    return {
      code: 'magic_bytes',
      message: `Could not read the first bytes of "${file.name}" to verify its format.`,
    };
  }

  const matched = matchMagicSignature(prefix);
  if (!matched) {
    return {
      code: 'magic_bytes',
      message: `"${file.name}" does not match any recognised .${ext} signature. The file may be renamed or corrupt.`,
    };
  }

  if (!matched.inferredExtensions.includes(ext)) {
    return {
      code: 'magic_bytes',
      message: `"${file.name}" claims to be a .${ext} but the binary signature looks like ${matched.name}. The file may have been renamed.`,
    };
  }

  return null;
}

/**
 * Run every check against a single file. Errors are blockers
 * (`valid: false`); warnings are non-blocking.
 *
 * When `rule` is null/undefined, returns a baseline `valid: true`
 * with empty errors/warnings — the no-rule case.
 */
export async function validateFile(
  file: File,
  rule: FileUploadRule | null | undefined,
): Promise<FileValidationResult> {
  if (!rule) {
    return { valid: true, errors: [], warnings: [] };
  }

  const errors: ValidationError[] = [];
  const warnings: ValidationWarning[] = [];

  const extError = validateExtension(file, rule.accepted_types_json);
  if (extError) errors.push(extError);

  // Skip MIME and magic-byte checks if the extension itself failed —
  // there's no useful signal once the basic type is wrong.
  if (!extError) {
    const mimeError = validateMime(file);
    if (mimeError) errors.push(mimeError);

    const magicWarning = await validateMagicBytes(file);
    if (magicWarning) warnings.push(magicWarning);
  }

  const sizeError = validateSize(file, rule.max_file_size_mb);
  if (sizeError) errors.push(sizeError);

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}
